import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, InputLabel, ListItemText, MenuItem, OutlinedInput, Radio, RadioGroup, Select, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import * as Yup from 'yup';
import { uwinAPI } from '../../../services/uwin.api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const NewbornRegistration = (props) => {
    const { benProfileData, pregnancyOutcomeData } = props;
    const navigate = useNavigate();
    const [addedChildren, setAddedChildren] = useState([]);
    const [submitType, setSubmitType] = useState('save_continue');

    // New state for Dialog handling
    const [openTwinDialog, setOpenTwinDialog] = useState(false);
    const [pendingSubmission, setPendingSubmission] = useState(null);

    const deliveryDate = pregnancyOutcomeData?.date_of_delivery ? dayjs(pregnancyOutcomeData?.date_of_delivery) : null;
    const isTooOld = deliveryDate ? dayjs().diff(deliveryDate, 'day') > 15 : false;

    const validationSchema = Yup.object().shape({
        type_of_birth: Yup.string().required('Type of Birth is required'),
        child_name: Yup.string().when("type_of_birth", {
            is: "live birth",
            then: (schema) =>
                schema
                    .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
                    .required("Child Name is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        dob: Yup.date().when("type_of_birth", {
            is: "live birth",
            then: (schema) => schema.required("Date of Birth is required"),
            otherwise: (schema) => schema.notRequired(),
        }),

        time_of_delivery: Yup.string().when("type_of_birth", {
            is: "live birth",
            then: (schema) => schema.required("Time of Birth is Required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        child_gender: Yup.string().when("type_of_birth", {
            is: "live birth",
            then: (schema) => schema.required("Gender is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        child_weight: Yup.string().when("type_of_birth", {
            is: "live birth",
            then: (schema) =>
                schema
                    .matches(/^\d+(\.\d+)?$/, "Invalid weight format")
                    .required("Child Weight at Birth is Required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        father_name: Yup.string().matches(/^[A-Za-z\s]*$/, 'Only alphabets are allowed'),
        baby_cried_immediately_after_birth: Yup.string().when("type_of_birth", {
            is: "live birth",
            then: (schema) =>
                schema.required("Baby cried immediately after birth is required"),
            otherwise: (schema) => schema.notRequired(),
        }),

        breastfeeding_started_within_hour: Yup.string().when("type_of_birth", {
            is: "live birth",
            then: (schema) =>
                schema.required("Breastfeeding started within hour is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        breastfeeding_started_within_hour_reason: Yup.string().when('breastfeeding_started_within_hour', {
            is: 'false',
            then: (schema) => schema.required('Reason is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        breastfeeding_started_within_hour_other_reason: Yup.string().when(['breastfeeding_started_within_hour', 'breastfeeding_started_within_hour_reason'], {
            is: (breastfeeding, reason) => breastfeeding === 'false' && reason === 'Other',
            then: (schema) => schema.required('Other reason is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        birth_visible_defects: Yup.string().when("type_of_birth", {
            is: "live birth",
            then: (schema) =>
                schema.required("Any visible birth defects is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        birth_visible_defects_reason: Yup.array().when('birth_visible_defects', {
            is: 'true',
            then: (schema) => schema.min(1, 'Required').required('Reason is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        birth_visible_defects_other_reason: Yup.string().when(['birth_visible_defects', 'birth_visible_defects_reason'], {
            is: (defects, reason) => defects === 'true' && reason?.includes('Other'),
            then: (schema) => schema.required('Required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        is_pvtg: Yup.string().when("type_of_birth", {
            is: "live birth",
            then: (schema) => schema.required("PVGT is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        still_birth_type: Yup.string().when('type_of_birth', {
            is: 'still birth',
            then: (schema) => schema.required('Still birth type is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const initialValues = {
        pregnancy_outcome_id: pregnancyOutcomeData?.pregnancy_outcome_id || "",
        beneficiary_id: benProfileData?.beneficiary_id || "",
        dob: pregnancyOutcomeData?.date_of_delivery || "",
        time_of_delivery: "",
        child_name: benProfileData?.ben_name ? `Baby of ${benProfileData?.ben_name}` : "",
        child_gender: "",
        child_weight: "",
        father_name: "",
        breastfeeding_started_within_hour: "",
        baby_cried_immediately_after_birth: "",
        breastfeeding_started_within_hour_reason: "",
        breastfeeding_started_within_hour_other_reason: "",
        birth_visible_defects: "",
        birth_visible_defects_reason: [],
        birth_visible_defects_other_reason: "",
        type_of_birth: "",
        is_pvtg: "",
        still_birth_type: ""
    };

    // Function to handle the actual API call and success/failure logic
    const executeSubmission = async (values, actions) => {
        try {
            const payload = {
                ...values,
                child_weight: parseFloat(values?.child_weight),
            };

            const response = await uwinAPI.addChildAgainstPregnancy(payload);
            if (response?.message === "Child details added Successfully") {
                toast.success(response?.message);
                if (submitType === 'save_continue') {
                    navigate("/detailsabha");
                } else {
                    setAddedChildren([...addedChildren, response?.data]);
                    actions.resetForm();
                }
            } else {
                toast.error(response?.message || "Failed to add child");
            }
        } catch (error) {
            console.error("API Error:", error);
            toast.error("An error occurred. Please try again.");
        } finally {
            actions.setSubmitting(false);
        }
    };

    // Formik onSubmit handler with conditional logic for Twin Dialog
    const handleFormSubmit = async (values, actions) => {
        // Check if user wants to add more AND there are already children added
        if (submitType === 'save_add_more' && addedChildren.length > 0) {
            setPendingSubmission({ values, actions });
            setOpenTwinDialog(true);
            // Do not call setSubmitting(false) here, wait for dialog response
        } else {
            await executeSubmission(values, actions);
        }
    };

    const handleDialogConfirm = () => {
        setOpenTwinDialog(false);
        if (pendingSubmission) {
            executeSubmission(pendingSubmission.values, pendingSubmission.actions);
            setPendingSubmission(null);
        }
    };

    const handleDialogCancel = () => {
        setOpenTwinDialog(false);
        if (pendingSubmission) {
            pendingSubmission.actions.setSubmitting(false);
            setPendingSubmission(null);
        }
    };

    if (isTooOld) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
                <h5>You can add upto only 15 days after the delivery</h5>
            </Box>
        );
    }

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleFormSubmit}
                enableReinitialize
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    isSubmitting,
                    submitForm,
                    resetForm
                }) => (
                    <Form>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <FormLabel id="type_of_birth-label">Type of Birth *</FormLabel>
                            <RadioGroup
                                row
                                aria-labelledby="type_of_birth-label"
                                name="type_of_birth"
                                value={values?.type_of_birth}
                                onChange={(e) => {
                                    resetForm(); // Clears everything to initialValues
                                    setFieldValue("type_of_birth", e.target.value);
                                }}
                            >
                                <FormControlLabel value="live birth" control={<Radio />} label="Live Birth" />
                                <FormControlLabel value="still birth" control={<Radio />} label="Still Birth" />
                            </RadioGroup>
                            {touched?.type_of_birth && errors?.type_of_birth && (
                                <FormHelperText error>{errors?.type_of_birth}</FormHelperText>
                            )}
                        </FormControl>

                        <Grid container spacing={3} sx={{ alignItems: "flex-start" }}>
                            {values?.type_of_birth === "live birth" &&
                                <>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            id="child_name"
                                            name="child_name"
                                            label="Child Name *"
                                            value={values?.child_name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched?.child_name && Boolean(errors?.child_name)}
                                            helperText={touched?.child_name && errors?.child_name}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label="Date of Birth *"
                                                format="DD-MM-YYYY"
                                                value={values?.dob ? dayjs(values?.dob) : null}
                                                minDate={deliveryDate}
                                                maxDate={dayjs()}
                                                onChange={(value) => {
                                                    const formatted = value ? value.format('YYYY-MM-DD') : null;
                                                    setFieldValue('dob', formatted);
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        autoComplete: "off",
                                                        error: touched?.dob && Boolean(errors?.dob),
                                                        helperText: touched?.dob && errors?.dob,
                                                        onBlur: handleBlur,
                                                        name: "dob",
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <MobileTimePicker
                                                label="Time of Birth (HH:MM) *"
                                                value={values?.time_of_delivery ? dayjs(values?.time_of_delivery, 'hh:mm A') : null}
                                                onChange={(value) => {
                                                    setFieldValue('time_of_delivery', value ? value.format('hh:mm A') : '');
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        autoComplete: "off",
                                                        error: touched?.time_of_delivery && Boolean(errors?.time_of_delivery),
                                                        helperText: touched?.time_of_delivery && errors?.time_of_delivery,
                                                        onBlur: handleBlur,
                                                        name: "time_of_delivery",
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <FormControl fullWidth error={touched?.child_gender && Boolean(errors?.child_gender)}>
                                            <FormLabel id="child_gender-label">Gender *</FormLabel>
                                            <RadioGroup
                                                row
                                                aria-labelledby="child_gender-label"
                                                name="child_gender"
                                                value={values?.child_gender}
                                                onChange={handleChange}
                                            >
                                                <FormControlLabel value="Male" control={<Radio />} label="Male" />
                                                <FormControlLabel value="Female" control={<Radio />} label="Female" />
                                                <FormControlLabel value="Others" control={<Radio />} label="Other" />
                                            </RadioGroup>
                                            {touched?.child_gender && errors?.child_gender && (
                                                <FormHelperText>{errors?.child_gender}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            id="child_weight"
                                            name="child_weight"
                                            label="Child Weight at Birth in KG's *"
                                            value={values?.child_weight}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched?.child_weight && Boolean(errors?.child_weight)}
                                            helperText={touched?.child_weight && errors?.child_weight}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            id="father_name"
                                            name="father_name"
                                            label="Father Name"
                                            value={values?.father_name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched?.father_name && Boolean(errors?.father_name)}
                                            helperText={touched?.father_name && errors?.father_name}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <FormControl fullWidth error={touched?.baby_cried_immediately_after_birth && Boolean(errors?.baby_cried_immediately_after_birth)}>
                                            <InputLabel id="baby_cried_immediately_after_birth-label">Baby Cried Immediately After Birth *</InputLabel>
                                            <Select
                                                labelId="baby_cried_immediately_after_birth-label"
                                                id="baby_cried_immediately_after_birth"
                                                name="baby_cried_immediately_after_birth"
                                                value={values?.baby_cried_immediately_after_birth}
                                                label="Baby Cried Immediately After Birth *"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            >
                                                <MenuItem value={"true"}>{"Yes"}</MenuItem>
                                                <MenuItem value={"false"}>{"No"}</MenuItem>
                                            </Select>
                                            {touched?.baby_cried_immediately_after_birth && errors?.baby_cried_immediately_after_birth && (
                                                <FormHelperText>{errors?.baby_cried_immediately_after_birth}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <FormControl fullWidth error={touched?.breastfeeding_started_within_hour && Boolean(errors?.breastfeeding_started_within_hour)}>
                                            <InputLabel id="breastfeeding_started_within_hour-label">Breastfeeding Starting Within 1 Hour *</InputLabel>
                                            <Select
                                                labelId="breastfeeding_started_within_hour-label"
                                                id="breastfeeding_started_within_hour"
                                                name="breastfeeding_started_within_hour"
                                                value={values?.breastfeeding_started_within_hour}
                                                label="Breastfeeding Starting Within 1 Hour *"
                                                onChange={(e) => {
                                                    handleChange(e);
                                                    if (e.target.value === "true") {
                                                        setFieldValue('breastfeeding_started_within_hour_reason', '');
                                                        setFieldValue('breastfeeding_started_within_hour_other_reason', '');
                                                    }
                                                }}
                                                onBlur={handleBlur}
                                            >
                                                <MenuItem value={"true"}>{"Yes"}</MenuItem>
                                                <MenuItem value={"false"}>{"No"}</MenuItem>
                                            </Select>
                                            {touched?.breastfeeding_started_within_hour && errors?.breastfeeding_started_within_hour && (
                                                <FormHelperText>{errors?.breastfeeding_started_within_hour}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    {values?.breastfeeding_started_within_hour === "false" &&
                                        <>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <FormControl
                                                    fullWidth
                                                    error={touched?.breastfeeding_started_within_hour_reason && Boolean(errors?.breastfeeding_started_within_hour_reason)}
                                                >
                                                    <InputLabel id="breastfeeding_started_within_hour_reason-label">Reason *</InputLabel>
                                                    <Select
                                                        labelId="breastfeeding_started_within_hour_reason-label"
                                                        id="breastfeeding_started_within_hour_reason"
                                                        name="breastfeeding_started_within_hour_reason"
                                                        value={values?.breastfeeding_started_within_hour_reason}
                                                        label="Reason *"
                                                        onChange={(e) => {
                                                            handleChange(e);
                                                            if (e.target.value !== "Other") {
                                                                setFieldValue('breastfeeding_started_within_hour_other_reason', '');
                                                            }
                                                        }}
                                                        onBlur={handleBlur}
                                                    >
                                                        <MenuItem value={"Mother Not Well"}>{"Mother Not Well"}</MenuItem>
                                                        <MenuItem value={"Infant Not Well"}>{"Infant Not Well"}</MenuItem>
                                                        <MenuItem value={"Mother Refused"}>{"Mother Refused"}</MenuItem>
                                                        <MenuItem value={"Other"}>{"Other"}</MenuItem>
                                                    </Select>
                                                    {touched?.breastfeeding_started_within_hour_reason && errors?.breastfeeding_started_within_hour_reason && (
                                                        <FormHelperText>{errors?.breastfeeding_started_within_hour_reason}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            </Grid>

                                            {values?.breastfeeding_started_within_hour_reason === "Other" &&
                                                <Grid size={{ xs: 12, md: 4 }}>
                                                    <TextField
                                                        fullWidth
                                                        variant="outlined"
                                                        id="breastfeeding_started_within_hour_other_reason"
                                                        name="breastfeeding_started_within_hour_other_reason"
                                                        label="Others Specify *"
                                                        value={values?.breastfeeding_started_within_hour_other_reason}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        // disabled={values?.breastfeeding_started_within_hour !== "false" || values?.breastfeeding_started_within_hour_reason !== "Other"}
                                                        error={touched?.breastfeeding_started_within_hour_other_reason && Boolean(errors?.breastfeeding_started_within_hour_other_reason)}
                                                        helperText={touched?.breastfeeding_started_within_hour_other_reason && errors?.breastfeeding_started_within_hour_other_reason}
                                                    />
                                                </Grid>
                                            }
                                        </>
                                    }

                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <FormControl fullWidth error={touched?.birth_visible_defects && Boolean(errors?.birth_visible_defects)}>
                                            <InputLabel id="birth_visible_defects-label">Any Visible Birth Defects *</InputLabel>
                                            <Select
                                                labelId="birth_visible_defects-label"
                                                id="birth_visible_defects"
                                                name="birth_visible_defects"
                                                value={values?.birth_visible_defects}
                                                label="Any Visible Birth Defects *"
                                                onChange={(e) => {
                                                    handleChange(e);
                                                    if (e.target.value === "false") {
                                                        setFieldValue('birth_visible_defects_reason', []);
                                                        setFieldValue('birth_visible_defects_other_reason', '');
                                                    }
                                                }}
                                                onBlur={handleBlur}
                                            >
                                                <MenuItem value={"true"}>{"Yes"}</MenuItem>
                                                <MenuItem value={"false"}>{"No"}</MenuItem>
                                            </Select>
                                            {touched?.birth_visible_defects && errors?.birth_visible_defects && (
                                                <FormHelperText>{errors?.birth_visible_defects}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    {values?.birth_visible_defects === "true" &&
                                        <>
                                            <Grid size={{ xs: 12, md: 4 }}>
                                                <FormControl
                                                    fullWidth
                                                    error={touched?.birth_visible_defects_reason && Boolean(errors?.birth_visible_defects_reason)}
                                                >
                                                    <InputLabel id="birth_visible_defects_reason-label">What is the Defect *</InputLabel>
                                                    <Select
                                                        labelId="birth_visible_defects_reason-label"
                                                        id="birth_visible_defects_reason"
                                                        name="birth_visible_defects_reason"
                                                        multiple
                                                        value={values?.birth_visible_defects_reason}
                                                        label="What is the Defect *"
                                                        input={<OutlinedInput label="What is the Defect *" />}
                                                        renderValue={(selected) => selected.join(', ')}
                                                        onChange={(e) => {
                                                            const { value } = e.target;
                                                            setFieldValue('birth_visible_defects_reason', typeof value === 'string' ? value.split(',') : value);
                                                            if (!value.includes('Other')) {
                                                                setFieldValue('birth_visible_defects_other_reason', '');
                                                            }
                                                        }}
                                                        onBlur={handleBlur}
                                                    >
                                                        {[
                                                            "Neural tube defects",
                                                            "Downs Syndrome",
                                                            "Cleft Lip",
                                                            "Palate",
                                                            "Club foot",
                                                            "Developmental dysplasia of the hip",
                                                            "Congenital Cataract",
                                                            "Congenital Deafness",
                                                            "Hydrocephalus",
                                                            "Imperforate Anus",
                                                            "Other"
                                                        ].map((name) => (
                                                            <MenuItem key={name} value={name}>
                                                                <Checkbox checked={values?.birth_visible_defects_reason.indexOf(name) > -1} />
                                                                <ListItemText primary={name} />
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {touched?.birth_visible_defects_reason && errors?.birth_visible_defects_reason && (
                                                        <FormHelperText>{errors?.birth_visible_defects_reason}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            </Grid>

                                            {values?.birth_visible_defects_reason.includes('Other') &&
                                                <Grid size={{ xs: 12, md: 4 }}>
                                                    <TextField
                                                        fullWidth
                                                        variant="outlined"
                                                        id="birth_visible_defects_other_reason"
                                                        name="birth_visible_defects_other_reason"
                                                        label="Others Specify *"
                                                        value={values?.birth_visible_defects_other_reason}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched?.birth_visible_defects_other_reason && Boolean(errors?.birth_visible_defects_other_reason)}
                                                        helperText={touched?.birth_visible_defects_other_reason && errors?.birth_visible_defects_other_reason}
                                                    />
                                                </Grid>
                                            }
                                        </>

                                    }

                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <FormControl fullWidth error={touched?.is_pvtg && Boolean(errors?.is_pvtg)}>
                                            <FormLabel id="is_pvtg-label">PVTG *</FormLabel>
                                            <RadioGroup
                                                row
                                                aria-labelledby="is_pvtg-label"
                                                name="is_pvtg"
                                                value={values?.is_pvtg}
                                                onChange={handleChange}
                                            >
                                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                                <FormControlLabel value="No" control={<Radio />} label="No" />
                                            </RadioGroup>
                                            {touched?.is_pvtg && errors?.is_pvtg && (
                                                <FormHelperText>{errors?.is_pvtg}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                </>
                            }

                            {values?.type_of_birth === "still birth" &&
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <FormControl fullWidth>
                                        <InputLabel id="still_birth_type-label">Still Birth Type</InputLabel>
                                        <Select
                                            labelId="still_birth_type-label"
                                            id="still_birth_type"
                                            name="still_birth_type"
                                            value={values?.still_birth_type}
                                            label="Still Birth Type"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched?.still_birth_type && Boolean(errors?.still_birth_type)}
                                        >
                                            <MenuItem value={"Fresh"}>{"Fresh"}</MenuItem>
                                            <MenuItem value={"Macerated"}>{"Macerated"}</MenuItem>
                                        </Select>
                                        {touched?.still_birth_type && errors?.still_birth_type && (
                                            <FormHelperText error>{errors?.still_birth_type}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                            }
                        </Grid>

                        {values?.type_of_birth && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button
                                    type='submit'
                                    variant="contained"
                                    color="primary"
                                    className='saveButton'
                                    disabled={isSubmitting}
                                    onClick={() => setSubmitType('save_continue')}
                                >
                                    Save & Continue
                                </Button>
                                <Button
                                    type='submit'
                                    variant="contained"
                                    color="primary"
                                    className='saveButton'
                                    disabled={isSubmitting}
                                    onClick={() => setSubmitType('save_add_more')}
                                >
                                    Save & Add More
                                </Button>
                            </Box>
                        )}
                    </Form>
                )
                }
            </Formik >

            {addedChildren.length > 0 && (
                <div className="viewSec" style={{ marginTop: '40px' }}>
                    {addedChildren.map((child, index) => (
                        <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: '4px' }}>
                            <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
                                <Grid item xs={12}>
                                    <h6>{child.type_of_birth} Child {index + 1} Details</h6>
                                </Grid>
                                <Grid item xs={6}>
                                    <h6 className="viewHead">Name of Child</h6>
                                    <p className="viewData">{child.child_name}</p>
                                </Grid>
                                <Grid item xs={6}>
                                    <h6 className="viewHead">Gender</h6>
                                    <p className="viewData">{child.child_gender}</p>
                                </Grid>
                            </Grid>
                        </Box>
                    ))}
                </div>
            )}

            {/* Twin Confirmation Dialog */}
            <Dialog
                open={openTwinDialog}
                onClose={handleDialogCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Confirm Details!"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Child is already registered, is it twin?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDialogConfirm} color="primary" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default NewbornRegistration;
